import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const MAX_ATTEMPTS = 12;
const POLL_MS = 2500;

export default function StripeReturn() {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id');
    const { refreshUser } = useAuth();
    const [state, setState] = useState('checking'); // checking | complete | expired | timeout | error
    const [result, setResult] = useState(null);
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) { setState('error'); return; }
        let stopped = false;
        const poll = async () => {
            if (stopped) return;
            attempts.current += 1;
            try {
                const { data } = await api.get(`/subscription/stripe/status/${sessionId}`);
                if (stopped) return;
                if (data.status === 'complete') {
                    setResult(data);
                    setState('complete');
                    try { await refreshUser(); } catch {}
                    return;
                }
                if (data.status === 'expired') { setState('expired'); return; }
            } catch {
                if (stopped) return;
            }
            if (attempts.current >= MAX_ATTEMPTS) { setState('timeout'); return; }
            setTimeout(poll, POLL_MS);
        };
        poll();
        return () => { stopped = true; };
    }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <main className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="page-stripe-return">
            {state === 'checking' && (
                <div data-testid="stripe-return-checking">
                    <Loader2 size={40} className="mx-auto animate-spin text-lq-purple-500" />
                    <h1 className="mt-6 font-display text-3xl font-semibold text-lq-ink-black">Confirming your payment&hellip;</h1>
                    <p className="mt-3 text-lq-ink-700">This usually takes a few seconds. Please don&rsquo;t close this page.</p>
                </div>
            )}
            {state === 'complete' && (
                <div data-testid="stripe-return-success">
                    <CheckCircle2 size={48} className="mx-auto text-lq-success" />
                    <h1 className="mt-6 font-display text-3xl font-semibold text-lq-ink-black">You&rsquo;re all set!</h1>
                    <p className="mt-3 text-lq-ink-700">
                        Your <strong className="capitalize">{result?.tier}</strong> plan is now {result?.status === 'trialing' ? 'in its 7-day free trial' : 'active'}.
                        Every puzzle type is unlocked and all adverts are switched off.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/dashboard" className="px-6 py-3 rounded-full bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold" data-testid="stripe-return-dashboard-btn">Go to dashboard</Link>
                        <Link to="/onboarding/add-child" className="px-6 py-3 rounded-full bg-lq-ink-100 hover:bg-lq-purple-100 text-lq-ink-900 font-semibold" data-testid="stripe-return-add-child-btn">Add another child</Link>
                    </div>
                </div>
            )}
            {state === 'expired' && (
                <div data-testid="stripe-return-expired">
                    <XCircle size={48} className="mx-auto text-lq-error" />
                    <h1 className="mt-6 font-display text-3xl font-semibold text-lq-ink-black">Checkout expired</h1>
                    <p className="mt-3 text-lq-ink-700">That payment session timed out before it was completed. No money was taken.</p>
                    <Link to="/pricing" className="mt-8 inline-block px-6 py-3 rounded-full bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold" data-testid="stripe-return-retry-btn">Back to pricing</Link>
                </div>
            )}
            {(state === 'timeout' || state === 'error') && (
                <div data-testid="stripe-return-timeout">
                    <Loader2 size={40} className="mx-auto text-lq-ink-500" />
                    <h1 className="mt-6 font-display text-3xl font-semibold text-lq-ink-black">Still processing</h1>
                    <p className="mt-3 text-lq-ink-700">
                        Your payment is taking a little longer than usual to confirm. If your card was charged, your plan will
                        activate automatically within a few minutes &mdash; check your <Link to="/account" className="text-lq-purple-700 font-semibold underline">account page</Link>.
                    </p>
                </div>
            )}
        </main>
    );
}
